import { useRequest } from '@hooks';
import { Input, Select } from 'antd';
import { useNotify } from 'react-admin';
import { useListContext } from 'ra-core';
import React, { useEffect, useRef, useState } from 'react';

import { useModalContext } from '@providers';
import { historicalPaymentHttp } from '@network';
import { BlueButton, BlueOutlineButton } from '@components';
import cls from './HistoricalPayment.module.css';

enum BulkProps {
  'True Payment Currency', // text
  'True Principal Payment', // integer
  'True Other Payment', // integer
  'True Tax Payment', // integer
  'True Rebate', // integer
  'True Payment Status', // integer
}

type BulkProp = keyof typeof BulkProps;

const bulkPropsList = Object.keys(BulkProps).filter(k => isNaN(+k) || isNaN(parseFloat(k)));

export const HistoricalPaymentActions = () => {
  const notify = useNotify();
  const { hideModal } = useModalContext();
  const { selectedIds, refetch } = useListContext();
  const textRef = useRef<any>();
  const intRef = useRef<any>();

  const [prop, setProp] = useState<BulkProp>();
  const [value, setValue] = useState<string | number | null>(null);

  const isText = 'True Payment Currency' === prop;
  const isInt = 'True Payment Currency' !== prop;

  const resetValues = () => {
    setValue(null);
  };

  const onCancel = () => {
    setProp(undefined);
    resetValues();
    hideModal();
  };

  const { loading, submit } = useRequest(async () => {
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      return notify('No records are selected');
    }
    if (!prop) {
      return notify('No property is selected');
    }

    const updatedTotal = await historicalPaymentHttp.updateBulk({ selectedIds, prop, value });

    if (selectedIds.length !== updatedTotal) {
      notify(`Updated: ${updatedTotal} of ${selectedIds.length} records. Some records can't be changed because of the contract status of related credit.`);
    } else {
      notify(`Updated records: ${updatedTotal}`);
    }

    onCancel();
    refetch();
  });

  const bulkPropsSorted = bulkPropsList
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  useEffect(() => {
    resetValues();
    prop && setTimeout(() => {
      textRef.current?.focus();
      intRef.current?.focus();
    }, 300);
  }, [prop]);

  return (
    <div className={cls.box}>
      <div className={cls.title}>Properties to update</div>
      <Select value={prop} onChange={v => setProp(v)} className={cls.select}
        placeholder="Select a property to edit">
        {bulkPropsSorted.map(propName => (
          <Select.Option key={propName} value={propName}>
            {propName}
          </Select.Option>
        ))}
      </Select>

      <div className={cls.content}>
        <div className={cls.title}>
          <div>{prop}</div>
        </div>
        {prop && isText ? <Input.TextArea
          className={cls.textarea}
          value={value || ''}
          onChange={e => setValue(e.target.value)}
          ref={textRef}
          autoFocus
        /> : null}
        {prop && isInt ? <Input
          type="number"
          className={cls.textarea}
          value={typeof value === 'number' ? value : undefined}
          onChange={e => setValue(+e.target.value)}
          ref={intRef}
          autoFocus
        /> : null}
      </div>

      <div className={cls.controls}>
        <BlueButton className={cls.btn} onClick={submit} disabled={loading}>Update</BlueButton>
        <BlueOutlineButton className={cls.btn} onClick={onCancel}>Cancel</BlueOutlineButton>
      </div>
    </div>
  );
};
